/*
 * VibeHome: post-onboarding home screen.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { api, type DailyAction, type DailyReturnV2, type PalProfileResponse } from '../cloud/api';
import { AskMojiThread } from './AskMojiThread';
import type { UseAskMojiThreadResult } from './hooks/useAskMojiThread';
import { DailyReturnStack } from './DailyReturnStack';
import { useVoiceOptIn, type UseVoiceOptInDeps } from './hooks/useVoiceOptIn';

const VibeMap = lazy(() => import('./VibeMap').then((module) => ({ default: module.VibeMap })));

export type HomeNavigationTarget = 'drive' | 'settings';

export function VibeHome({ pal, onPalUpdated, askMoji, onNavigate, voiceOptInDeps }: {
    pal: PalProfileResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
    askMoji: UseAskMojiThreadResult;
    onNavigate: (target: HomeNavigationTarget) => void;
    voiceOptInDeps?: UseVoiceOptInDeps;
}) {
    const [daily, setDaily] = useState<DailyReturnV2 | null>(null);
    const [showMap, setShowMap] = useState(false);
    const voice = useVoiceOptIn(voiceOptInDeps);

    useEffect(() => {
        let cancelled = false;
        setDaily(null);
        void api.dailyReturn()
            .then((next) => { if (!cancelled) setDaily(next); })
            .catch(() => { if (!cancelled) setDaily(null); });
        return () => { cancelled = true; };
    }, [pal.pal_id]);

    function handleDailyAction(action: DailyAction) {
        switch (action.kind) {
            case 'drive':
                onNavigate('drive');
                break;
            case 'map':
                setShowMap(true);
                break;
            case 'ask_moji':
                askMoji.setQuery(action.prompt ?? '');
                break;
            case 'settings':
                onNavigate('settings');
                break;
        }
    }

    const voiceSetupVisible = voice.supported
        && !voice.loading
        && (!voice.optIn || voice.permission !== 'granted');

    return (
        <div className="space-y-4">
            {daily && (
                <DailyReturnStack
                    palId={pal.pal_id}
                    daily={daily}
                    voiceSetup={{
                        visible: voiceSetupVisible,
                        action: { kind: 'settings', label: 'Set up voice', target: 'voice' },
                    }}
                    onAction={handleDailyAction}
                />
            )}
            <PalCard pal={pal} />
            <MapEntryCard onOpen={() => setShowMap(true)} />
            <VibeSpotsModule />
            <AskMojiThread pal={pal} onPalUpdated={onPalUpdated} {...askMoji} />
            {showMap && (
                <Suspense fallback={<div className="fixed inset-0 z-50 bg-surface p-5">Loading map...</div>}>
                    <VibeMap onClose={() => setShowMap(false)} />
                </Suspense>
            )}
        </div>
    );
}

function MapEntryCard({ onOpen }: { onOpen: () => void }) {
    return (
        <div className="card">
            <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                    <h3 className="font-semibold">Vibe Map</h3>
                    <p className="muted text-sm">Heat cells, curated nodes, and your saved places.</p>
                </div>
                <button type="button" className="btn-primary shrink-0" onClick={onOpen}>
                    Open
                </button>
            </div>
        </div>
    );
}

function PalCard({ pal }: { pal: PalProfileResponse }) {
    const formLabel = formatForm(pal.profile.active_form);
    const ageLabel = pal.pal_age_days === 0
        ? 'just born'
        : pal.pal_age_days === 1
            ? '1 day old'
            : `${pal.pal_age_days} days old`;

    return (
        <div className="card">
            <div className="flex items-baseline gap-3 mb-2">
                <h2 className="text-xl font-semibold">{pal.pal_name}</h2>
                <span className="muted text-sm">{ageLabel}</span>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
                <Badge>{formLabel}</Badge>
                <Badge>tone - {pal.profile.tone_preference}</Badge>
                <Badge>privacy - {pal.profile.privacy_mode}</Badge>
                {pal.glow > 0 && <Badge>glow {pal.glow}</Badge>}
                {pal.marks_count > 0 && <Badge>marks {pal.marks_count}</Badge>}
            </div>
            {pal.profile.current_goals.length > 0 && (
                <div className="mt-3">
                    <div className="muted text-xs mb-1">working on</div>
                    <ul className="text-sm space-y-0.5">
                        {pal.profile.current_goals.slice(0, 3).map((g, i) => (
                            <li key={i}>- {g}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function VibeSpotsModule() {
    const [query, setQuery] = useState('');
    const [items, setItems] = useState<Array<{ id: string; name: string; domain: string; locality: string | null }>>([]);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [saved, setSaved] = useState<Set<string>>(() => new Set());
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const q = query.trim();
        if (q.length < 2) {
            setItems([]);
            setError(null);
            return;
        }
        const t = window.setTimeout(() => {
            void api.nodeSearch({ domain: 'cafe', q, limit: 6 })
                .then((r) => {
                    if (!cancelled) {
                        setItems(r.items);
                        setError(null);
                    }
                })
                .catch((err) => {
                    if (!cancelled) setError((err as Error).message);
                });
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [query]);

    async function save(nodeId: string) {
        setBusyId(nodeId);
        try {
            await api.nodeStamp(nodeId, 'save');
            setSaved((prev) => new Set(prev).add(nodeId));
            setError(null);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div className="card">
            <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-semibold">Vibe Spots</h3>
                <span className="muted text-xs">save privately</span>
            </div>
            <input
                type="text"
                className="w-full p-2 rounded-lg border border-line mb-2 text-sm"
                placeholder="Find a quiet cafe..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />
            {error && <p className="text-danger text-xs mb-2">{error}</p>}
            {items.length > 0 && (
                <div className="border border-line rounded-lg divide-y divide-line overflow-hidden text-sm">
                    {items.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 px-3 py-2">
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{item.name}</div>
                                {item.locality && <div className="muted text-xs truncate">{item.locality}</div>}
                            </div>
                            <button
                                type="button"
                                className="btn-secondary text-xs"
                                onClick={() => save(item.id)}
                                disabled={busyId === item.id || saved.has(item.id)}
                            >
                                {saved.has(item.id) ? 'Saved' : busyId === item.id ? '...' : 'Save'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Badge({ children }: { children: React.ReactNode }) {
    return (
        <span className="px-2 py-0.5 rounded-full bg-surface-raised border border-line">
            {children}
        </span>
    );
}

function formatForm(f: string): string {
    return f.split('_').map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
}
